import { useEffect, useRef, useState } from "react";

type Props = {
  lat: number;
  lng: number;
  label?: string;
  onChange?: (lat: number, lng: number) => void;
  height?: number;
};

/**
 * Malý náhled polohy s draggable markerem (Leaflet + OpenStreetMap).
 * Načítá se dynamicky, aby nezvětšoval initial bundle a fungoval s SSR off.
 */
export function LocationPreviewMap({ lat, lng, label, onChange, height = 220 }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const markerRef = useRef<any>(null);
  const [L, setL] = useState<any>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const leaflet = await import("leaflet");
      await import("leaflet/dist/leaflet.css");
      if (!cancelled) setL(leaflet);
    })();
    return () => { cancelled = true; };
  }, []);

  // init
  useEffect(() => {
    if (!L || !containerRef.current || mapRef.current) return;
    const map = L.map(containerRef.current, {
      center: [lat, lng],
      zoom: 15,
      scrollWheelZoom: false,
    });
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 19,
    }).addTo(map);
    const marker = L.marker([lat, lng], { draggable: !!onChange }).addTo(map);
    if (label) marker.bindTooltip(label, { permanent: false });
    if (onChange) {
      marker.on("dragend", () => {
        const p = marker.getLatLng();
        onChange(p.lat, p.lng);
      });
      map.on("click", (e: any) => {
        marker.setLatLng(e.latlng);
        onChange(e.latlng.lat, e.latlng.lng);
      });
    }
    mapRef.current = map;
    markerRef.current = marker;
    // Fix layout when container becomes visible
    setTimeout(() => map.invalidateSize(), 50);
    return () => {
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
    };
  }, [L]);

  // update position when props change
  useEffect(() => {
    if (!mapRef.current || !markerRef.current) return;
    markerRef.current.setLatLng([lat, lng]);
    mapRef.current.setView([lat, lng], mapRef.current.getZoom());
  }, [lat, lng]);

  return (
    <div
      ref={containerRef}
      className="w-full overflow-hidden rounded-md border"
      style={{ height }}
    />
  );
}
